import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import DataTable from 'react-data-table-component'
import moment from 'moment'
import { Link, useNavigate } from 'react-router-dom'
import { useModuleSettings } from '../../context/adminSettingsContext'
import { hasPermission } from '../../common/permissions'
import Swal from 'sweetalert2'
import { allFaqs, changeFaqStatus, deleteFaqs } from '../../slice/faqSlice'

const FaqList = () => {
    const dispatch = useDispatch()
    const { faqList } = useSelector(state => state.faq)
    const navigate = useNavigate()

    const { page, setPage, limit, setLimit } = useModuleSettings()

    const [statusFilter, setStatusFilter] = useState('active')
    const [searchQuery, setSearchQuery] = useState('')
    const [searchDebounce, setSearchDebounce] = useState('')
    const [expandedRows, setExpandedRows] = useState({})
    const [lang, setLang] = useState('en') // en | ar

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchDebounce(searchQuery)
            setPage(1)
        }, 500)
        return () => clearTimeout(timer)
    }, [searchQuery])

    // API call
    useEffect(() => {
        dispatch(allFaqs({
            page,
            limit,
            filter: statusFilter !== 'all' ? statusFilter : undefined,
            search: searchDebounce || undefined
        }))
    }, [dispatch, page, limit, statusFilter, searchDebounce])

    // Toggle accordion
    const toggleRow = (id) => {
        setExpandedRows(prev => ({
            ...prev,
            [id]: !prev[id]
        }))
    }

    // Status toggle
    const handleToggleStatus = async (id) => {
        const res = await dispatch(changeFaqStatus({ id }))
        if (res?.payload?.code === 200) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'success',
                title: 'Status updated',
                timer: 1500,
                showConfirmButton: false,
                backdrop: false,
            })
            dispatch(allFaqs({ page, limit, filter: statusFilter, search: searchDebounce }))
        }
    }

    // Delete
    const handleDelete = async (id) => {
        const confirm = await Swal.fire({
            title: 'Delete FAQ?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
        })

        if (!confirm.isConfirmed) return

        const res = await dispatch(deleteFaqs({ id }))
        if (res?.payload?.code === 200) {
            Swal.fire({
                icon: 'success',
                title: 'Deleted',
                timer: 1500,
                showConfirmButton: false
            })
            dispatch(allFaqs({ page, limit, filter: statusFilter, search: searchDebounce }))
        }
    }

    const handleEdit = (id) => {
        navigate('/faq/edit', { state: { id } })
    }

    // Expanded Answer (Accordion)
    const ExpandedComponent = ({ data }) => (
        <div
            style={{
                padding: '15px',
                background: '#f8f9fa',
                direction: lang === 'ar' ? 'rtl' : 'ltr',
                textAlign: lang === 'ar' ? 'right' : 'left'
            }}
        >
            <strong>
                {lang === 'ar' ? 'الإجابة:' : 'Answer:'}
            </strong>
            <div style={{ marginTop: '5px', whiteSpace: 'pre-wrap' }}>
                {lang === 'ar'
                    ? data.answer_ar || data.answer
                    : data.answer}
            </div>
        </div>
    )

    const columns = [
        {
            name: '',
            width: '60px',
            cell: row => (
                <button
                    className="btn btn-sm btn-light"
                    onClick={() => toggleRow(row.id)}
                >
                    <i className={`fe-${expandedRows[row.id] ? 'chevron-up' : 'chevron-down'}`} />
                </button>
            )
        },
        {
            name: 'ID',
            selector: row => row.id,
            width: '80px'
        },
        {
            name: lang === 'ar' ? 'السؤال' : 'Question',
            selector: row =>
                lang === 'ar'
                    ? row.question_ar || row.question
                    : row.question,
            minWidth: '350px',
            wrap: true
        },
        {
            name: lang === 'ar' ? 'الحالة' : 'Status',
            width: '120px',
            center: true,
            cell: row => (
                <div className="form-check form-switch">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        checked={row.is_active === 1}
                        onChange={() => handleToggleStatus(row.id)}
                        disabled={!hasPermission('faq', 'edit')}
                    />
                </div>
            )
        },
        {
            name: lang === 'ar' ? 'تاريخ الإنشاء' : 'Created',
            selector: row =>
                row.createdAt
                    ? moment(row.createdAt).format('DD MMM YYYY')
                    : 'NA',
            width: '150px'
        },
        {
            name: lang === 'ar' ? 'إجراء' : 'Action',
            width: '120px',
            center: true,
            cell: row => (
                <div className="d-flex gap-2">
                    {hasPermission('faq', 'edit') && (
                        <button
                            className="btn btn-sm btn-outline-warning"
                            onClick={() => handleEdit(row.id)}
                        >
                            <i className="fe-edit" />
                        </button>
                    )}

                    {hasPermission('faq', 'delete') && (
                        <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(row.id)}
                        >
                            <i className="fe-trash-2" />
                        </button>
                    )}
                </div>
            )
        }
    ]

    return (
        <div className="container-fluid px-4 py-3">

            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4>FAQs</h4>

                <div className="d-flex gap-2">
                    {/* Language Switch */}
                    {/* <select
                        className="form-select"
                        style={{ width: '120px' }}
                        value={lang}
                        onChange={(e) => setLang(e.target.value)}
                    >
                        <option value="en">English</option>
                        <option value="ar">Arabic</option>
                    </select> */}

                    {hasPermission('faq', 'add') && (
                        <Link to="/faq/add" className="btn btn-success">
                            + Add FAQ
                        </Link>
                    )}
                </div>
            </div>

            {/* Search + Filter */}
            <div className="card mb-3 p-3">
                <div className="row">
                    <div className="col-md-8">
                        <input
                            className="form-control"
                            placeholder={lang === 'ar'
                                ? 'البحث في السؤال أو الإجابة...'
                                : 'Search question or answer...'}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>

                    <div className="col-md-4">
                        <select
                            className="form-select"
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value)
                                setPage(1)
                            }}
                        >
                            <option value="all">{lang === 'ar' ? 'الكل' : 'All'}</option>
                            <option value="active">{lang === 'ar' ? 'نشط' : 'Active'}</option>
                            <option value="inactive">{lang === 'ar' ? 'غير نشط' : 'Inactive'}</option>
                            <option value="deleted">{lang === 'ar' ? 'محذوف' : 'Deleted'}</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Table */}
            <DataTable
                columns={columns}
                data={faqList?.data || []}
                pagination
                paginationServer
                paginationTotalRows={faqList?.paging?.total || 0}
                paginationPerPage={limit}
                paginationDefaultPage={faqList?.paging?.page_no || 1}
                onChangeRowsPerPage={(newLimit) => { setLimit(newLimit); setPage(1) }}
                onChangePage={(newPage) => setPage(newPage)}
                expandableRows
                expandableRowsComponent={ExpandedComponent}
                expandableRowExpanded={row => expandedRows[row.id]}
                highlightOnHover
                responsive
                direction={lang === 'ar' ? 'rtl' : 'ltr'}
            />
        </div>
    )
}

export default FaqList